import api from '../axiosConfig';
import { RESTAURANT } from '../config/apiEndpoints';

export const RestaurantService = {
    /**
     * List all menu categories with pagination
     * GET /api/v1/restaurant/categories
     * @param {Object} params - Query parameters (page, limit)
     */
    getMenuCategories: async (params = {}) => {
        try {
            const axiosParams = { ...params };
            if (params.page !== undefined) {
                axiosParams.page = params.page;
            } else if (params.noPagination || params.noPaging) {
                axiosParams.noPagination = true;
            } else {
                axiosParams.noPagination = true;
            }
            const response = await api.get(RESTAURANT.CATEGORIES, { params: axiosParams });
            return response.data;
        } catch (error) {
            console.error('RestaurantService getMenuCategories error:', error);
            throw error;
        }
    },

    /**
     * Create a new menu category
     * POST /api/v1/restaurant/categories
     * @param {Object} data - Category payload
     */
    createMenuCategory: async (data) => {
        try {
            const response = await api.post(RESTAURANT.CATEGORIES, data);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('RestaurantService createMenuCategory error:', error);
            throw error;
        }
    },

    /**
     * Update an existing menu category
     * PUT /api/v1/restaurant/categories/{id}
     * @param {string} id - Category ID
     * @param {Object} data - Payload containing name and isActive
     */
    updateMenuCategory: async (id, data) => {
        try {
            const response = await api.put(`${RESTAURANT.CATEGORIES}/${id}`, data);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('RestaurantService updateMenuCategory error:', error);
            throw error;
        }
    },

    /**
     * List all menu items with pagination and filters
     * GET /api/v1/restaurant/items
     * @param {Object} params - Query parameters (page, limit, categoryId, search, isAvailable)
     */
    getMenuItems: async (params = {}) => {
        try {
            const axiosParams = { ...params };
            if (params.page !== undefined) {
                axiosParams.page = params.page;
            } else if (params.noPagination || params.noPaging) {
                axiosParams.noPagination = true;
            } else {
                axiosParams.noPagination = true;
            }
            const response = await api.get(RESTAURANT.ITEMS, { params: axiosParams });
            return response.data;
        } catch (error) {
            console.error('RestaurantService getMenuItems error:', error);
            throw error;
        }
    },

    /**
     * Create a new menu item
     * POST /api/v1/restaurant/items
     * @param {Object} data - Menu item payload
     */
    createMenuItem: async (data) => {
        try {
            const response = await api.post(RESTAURANT.ITEMS, data);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('RestaurantService createMenuItem error:', error);
            throw error;
        }
    },

    /**
     * Update an existing menu item (Price and Availability only)
     * PUT /api/v1/restaurant/items/{id}
     * @param {string} id - Menu item ID
     * @param {Object} data - Payload containing price and isAvailable
     */
    updateMenuItem: async (id, data) => {
        try {
            const response = await api.put(`${RESTAURANT.ITEMS}/${id}`, data);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('RestaurantService updateMenuItem error:', error);
            throw error;
        }
    },

    /**
     * Change a menu item's visibility status
     * PATCH /api/v1/restaurant/items/{id}/status
     * @param {string} id - Menu item ID
     * @param {boolean} isActive - New visibility status
     */
    changeMenuItemStatus: async (id, isActive) => {
        try {
            const response = await api.patch(`${RESTAURANT.ITEMS}/${id}/status`, { isActive });
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('RestaurantService changeMenuItemStatus error:', error);
            throw error;
        }
    },

    /**
     * Create a new restaurant POS order linked to a guest room
     * POST /api/v1/restaurant/orders
     * @param {Object} data - Order payload (roomId, items array)
     */
    createRestaurantOrder: async (data) => {
        try {
            const response = await api.post(RESTAURANT.ORDERS, data);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('RestaurantService createRestaurantOrder error:', error);
            throw error;
        }
    },

    /**
     * List all restaurant orders with pagination and filters
     * GET /api/v1/restaurant/orders
     * @param {Object} params - Query parameters (page, limit, status, date, search)
     */
    getRestaurantOrders: async (params = {}) => {
        try {
            const axiosParams = { ...params };
            if (params.page !== undefined) {
                axiosParams.page = params.page;
            } else if (params.noPagination || params.noPaging) {
                axiosParams.noPagination = true;
            } else {
                axiosParams.noPagination = true;
            }
            const response = await api.get(RESTAURANT.ORDERS, { params: axiosParams });
            return response.data;
        } catch (error) {
            console.error('RestaurantService getRestaurantOrders error:', error);
            throw error;
        }
    },

    /**
     * Get details of a specific restaurant order
     * GET /api/v1/restaurant/orders/{id}
     * @param {string} id - Order ID
     */
    getRestaurantOrderDetails: async (id) => {
        try {
            const response = await api.get(`${RESTAURANT.ORDERS}/${id}`);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('RestaurantService getRestaurantOrderDetails error:', error);
            throw error;
        }
    },

    /**
     * Cancel a specific restaurant order and void its ticket
     * POST /api/v1/restaurant/orders/{id}/cancel
     * @param {string} id - Order ID
     * @param {string} cancelReason - Reason for cancellation
     */
    cancelRestaurantOrder: async (id, cancelReason) => {
        try {
            const response = await api.post(`${RESTAURANT.ORDERS}/${id}/cancel`, { cancelReason });
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('RestaurantService cancelRestaurantOrder error:', error);
            throw error;
        }
    },

    /**
     * Update the status of a specific restaurant order
     * PATCH /api/v1/restaurant/orders/{id}/status
     * @param {string} id - Order ID
     * @param {string} status - New order status (e.g., 'In Progress', 'Ready', 'Completed')
     */
    updateOrderStatus: async (id, status) => {
        try {
            const response = await api.patch(`${RESTAURANT.ORDERS}/${id}/status`, { status });
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('RestaurantService updateOrderStatus error:', error);
            throw error;
        }
    },

    /**
     * List all restaurant operating hours
     * GET /api/v1/restaurant/operating-hours
     */
    getOperatingHours: async () => {
        try {
            const response = await api.get(RESTAURANT.OPERATING_HOURS);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('RestaurantService getOperatingHours error:', error);
            throw error;
        }
    },

    /**
     * Update restaurant operating hours and holiday schedules
     * PUT /api/v1/restaurant/operating-hours
     * @param {Object} payload - { hours: [...] }
     */
    updateOperatingHours: async (payload) => {
        try {
            const response = await api.put(RESTAURANT.OPERATING_HOURS, payload);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('RestaurantService updateOperatingHours error:', error);
            throw error;
        }
    },

    /**
     * List all restaurant tables with pagination
     * GET /api/v1/restaurant/tables
     * @param {Object} params - Query parameters (page, limit)
     */
    getRestaurantTables: async (params = {}) => {
        try {
            const axiosParams = { ...params };
            if (params.page !== undefined) {
                axiosParams.page = params.page;
            } else if (params.noPagination || params.noPaging) {
                axiosParams.noPagination = true;
            } else {
                axiosParams.noPagination = true;
            }
            const response = await api.get(RESTAURANT.TABLES, { params: axiosParams });
            return response.data;
        } catch (error) {
            console.error('RestaurantService getRestaurantTables error:', error);
            throw error;
        }
    },

    /**
     * Create a new physical restaurant table resource
     * POST /api/v1/restaurant/tables
     * @param {Object} data - Table payload (tableNumber, section, capacity, status, isActive)
     */
    createRestaurantTable: async (data) => {
        try {
            const response = await api.post(RESTAURANT.TABLES, data);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('RestaurantService createRestaurantTable error:', error);
            throw error;
        }
    },

    /**
     * Update an existing restaurant table
     * PUT /api/v1/restaurant/tables/{id}
     * @param {string} id - Table ID
     * @param {Object} payload - Table data
     */
    updateRestaurantTable: async (id, payload) => {
        try {
            const response = await api.put(`${RESTAURANT.TABLES}/${id}`, payload);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('RestaurantService updateRestaurantTable error:', error);
            throw error;
        }
    },

    /**
     * Get restaurant revenue report metrics
     * GET /api/v1/restaurant/reports/revenue
     */
    getRestaurantRevenueReport: async () => {
        try {
            const response = await api.get(RESTAURANT.REPORTS_REVENUE);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('RestaurantService getRestaurantRevenueReport error:', error);
            throw error;
        }
    }
};
